import { DateTime } from 'luxon';

/**
 * Formats a date to 'Mon, 29. Oct 2024.'
 * @param date DateTime object or string
 * @returns Formatted date string or 'Invalid Date'
 */
export const formatToWeekdayMonthDayYear = (date: DateTime | string): string => {
    const parsedDate = DateTime.isDateTime(date) ? date : DateTime.fromISO(date);
    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('EEE, dd. LLL yyyy.');
};

/**
 * Formats a date to '29. Oct 2024.'
 * @param date DateTime object or string
 * @returns Formatted date string or 'Invalid Date'
 */
export const formatToMonthDayYear = (date: DateTime | string): string => {
    const parsedDate = DateTime.isDateTime(date) ? date : DateTime.fromISO(date);
    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('dd. LLL yyyy.');
};

/**
 * Formats a date to '29. Oct.'
 * @param date DateTime object or string
 * @returns Formatted date string or 'Invalid Date'
 */
export const formatToMonthDay = (date: DateTime | string): string => {
    const parsedDate = DateTime.isDateTime(date) ? date : DateTime.fromISO(date);
    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('dd. LLL');
};

/**
 * Formats a date to 'December 7, 2024'
 * @param date DateTime object or string
 * @returns Formatted date string or 'Invalid Date'
 */
export const formatToFullMonthDayYear = (date: DateTime | string): string => {
    const parsedDate = DateTime.isDateTime(date) ? date : DateTime.fromISO(date);
    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('MMMM d, yyyy');
};

/**
 * Formats a date to 'DD.MM.YYYY'
 * @param date DateTime object, string, or JavaScript Date
 * @returns Formatted date string
 */
export const formatDateToDayMonthYear = (date: DateTime | string | Date): string => {
    let parsedDate: DateTime;

    if (DateTime.isDateTime(date)) {
        parsedDate = date;
    } else if (date instanceof Date) {
        parsedDate = DateTime.fromJSDate(date);
    } else {
        parsedDate = DateTime.fromISO(date);
    }

    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('dd.MM.yyyy');
};

/**
 * Formats a date with time to 'DD/MM/YYYY HH:mm'
 * @param date DateTime object, string, or JavaScript Date
 * @returns Formatted date and time string
 */
export const formatDateAndTime = (date: DateTime | string | Date): string => {
    let parsedDate: DateTime;

    if (DateTime.isDateTime(date)) {
        parsedDate = date;
    } else if (date instanceof Date) {
        parsedDate = DateTime.fromJSDate(date);
    } else {
        parsedDate = DateTime.fromISO(date);
    }

    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('dd/MM/yyyy HH:mm');
};

/**
 * Formats a time to 'HH:mm AM/PM'
 * @param time DateTime object, string, or JavaScript Date
 * @returns Formatted date string
 */
export const formatToAmPm = (time: string, date?: string): string => {
    const iso = date ? `${date}T${time}Z` : `1970-01-01T${time}Z`;

    return new Date(iso).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

/**
 * Formats a date to 'DD-MM-YYYY'
 * @param date DateTime object, string, or JavaScript Date
 * @returns Formatted date string
 */
export const formatDateToDayMonthYearWithDashes = (date: DateTime | string | Date): string => {
    let parsedDate: DateTime;

    if (DateTime.isDateTime(date)) {
        parsedDate = date;
    } else if (date instanceof Date) {
        parsedDate = DateTime.fromJSDate(date);
    } else {
        parsedDate = DateTime.fromISO(date);
    }

    if (!parsedDate.isValid) return 'Invalid Date';

    return parsedDate.toFormat('dd-MM-yyyy');
};
